import moment from 'moment';
import { z } from 'zod';
import { ZONE_COLORS } from '../../../users/data';
import { CHECKLIST_NOTES_RESET_TYPES } from './types';
import type {
  BaseNodeFields,
  BranchNodeFormValues,
  NetworkNodeFormValues,
} from './types';

const zoneColorValues = new Set(ZONE_COLORS.map((item) => item.color));

const requiredString = z.string({ error: 'שדה חובה!' }).min(1, 'שדה חובה!');

const HH_MM_REQUIRED_MSG = 'שדה חובה!';
const HH_MM_INVALID_MSG = 'יש להגדיר שעה תקינה';
const HH_MM_ORDER_MSG = 'שעת הסיום חייבת להיות אחרי שעת ההתחלה';

function isValidHhMm(value: string): boolean {
  if (!value?.length) return false;
  const m = moment(value, 'HH:mm', true);
  if (!m.isValid()) return false;
  const [hours, minutes] = value.split(':');
  return /^\d+$/.test(hours) && /^\d+$/.test(minutes);
}

function refineHhMmRange(
  start: string,
  end: string,
  ctx: z.RefinementCtx,
  pathPrefix: (string | number)[],
) {
  if (!start?.length) {
    ctx.addIssue({
      code: 'custom',
      message: HH_MM_REQUIRED_MSG,
      path: [...pathPrefix, 'startTime'],
    });
  } else if (!isValidHhMm(start)) {
    ctx.addIssue({
      code: 'custom',
      message: HH_MM_INVALID_MSG,
      path: [...pathPrefix, 'startTime'],
    });
  }

  if (!end?.length) {
    ctx.addIssue({
      code: 'custom',
      message: HH_MM_REQUIRED_MSG,
      path: [...pathPrefix, 'endTime'],
    });
  } else if (!isValidHhMm(end)) {
    ctx.addIssue({
      code: 'custom',
      message: HH_MM_INVALID_MSG,
      path: [...pathPrefix, 'endTime'],
    });
  }

  if (
    start?.length &&
    end?.length &&
    isValidHhMm(start) &&
    isValidHhMm(end) &&
    start == end
  ) {
    ctx.addIssue({
      code: 'custom',
      message: HH_MM_ORDER_MSG,
      path: [...pathPrefix, 'endTime'],
    });
  }
}

function refineShiftHhMmRange(
  from: string,
  to: string,
  ctx: z.RefinementCtx,
  pathPrefix: (string | number)[],
) {
  if (!from?.length) {
    ctx.addIssue({
      code: 'custom',
      message: HH_MM_REQUIRED_MSG,
      path: [...pathPrefix, 'from'],
    });
  } else if (!isValidHhMm(from)) {
    ctx.addIssue({
      code: 'custom',
      message: HH_MM_INVALID_MSG,
      path: [...pathPrefix, 'from'],
    });
  }

  if (!to?.length) {
    ctx.addIssue({
      code: 'custom',
      message: HH_MM_REQUIRED_MSG,
      path: [...pathPrefix, 'to'],
    });
  } else if (!isValidHhMm(to)) {
    ctx.addIssue({
      code: 'custom',
      message: HH_MM_INVALID_MSG,
      path: [...pathPrefix, 'to'],
    });
  }

  if (
    from?.length &&
    to?.length &&
    isValidHhMm(from) &&
    isValidHhMm(to) &&
    from == to
  ) {
    ctx.addIssue({
      code: 'custom',
      message: HH_MM_ORDER_MSG,
      path: [...pathPrefix, 'to'],
    });
  }
}

const phoneSchema = z
  .string({ error: 'שדה חובה!' })
  .min(1, 'שדה חובה!')
  .regex(/^\d+$/, {
    message: 'מספר הטלפון חייב להכיל ספרות בלבד',
  })
  .refine((value) => value.length === 9 || value.length === 10, {
    message: 'מספר טלפון חייב להיות באורך 9 או 10 ספרות (ללא קידומת בינלאומית)',
  });

const optionalWebsiteSchema = z.union([
  z.literal(''),
  z.url({ error: 'כתובת אתר לא תקינה' }),
]);

const contactSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  role: z.string(),
  phone: z.string(),
  email: z.string(),
});

const contactsSchema = z.array(contactSchema).superRefine((contacts, ctx) => {
  contacts.forEach((contact, index) => {
    const isRequired = index === 0;

    if (isRequired && !contact.firstName?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'שדה חובה!',
        path: [index, 'firstName'],
      });
    }

    if (isRequired && !contact.lastName?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'שדה חובה!',
        path: [index, 'lastName'],
      });
    }

    if (isRequired && !contact.role?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'שדה חובה!',
        path: [index, 'role'],
      });
    }

    if (isRequired || contact.phone?.length) {
      const phoneResult = phoneSchema.safeParse(contact.phone);
      if (!phoneResult.success) {
        ctx.addIssue({
          code: 'custom',
          message: phoneResult.error.issues[0]?.message ?? 'שדה חובה!',
          path: [index, 'phone'],
        });
      }
    }

    if (isRequired || contact.email?.length) {
      if (!contact.email?.length) {
        ctx.addIssue({
          code: 'custom',
          message: 'שדה חובה!',
          path: [index, 'email'],
        });
      } else if (!z.email().safeParse(contact.email).success) {
        ctx.addIssue({
          code: 'custom',
          message: 'כתובת מייל לא תקינה',
          path: [index, 'email'],
        });
      }
    }
  });
});

export const departmentSchema = z.object({
  id: z.string().min(1),
  name: requiredString,
  icon: requiredString,
  color: requiredString,
});

export const roleSchema = z.object({
  id: z.string().min(1),
  departmentId: requiredString,
  color: requiredString,
  icon: requiredString,
  roleType: requiredString,
  permissionLevel: requiredString,
  customPermission: z.boolean(),
  permissions: z.array(requiredString).min(1, 'שדה חובה!'),
});

export const shiftSchema = z.object({
  id: z.string().min(1),
  from: requiredString,
  to: requiredString,
  name: requiredString,
});

export const zoneSchema = z.object({
  id: z.string().min(1),
  name: requiredString,
  shortName: requiredString,
  color: requiredString,
});

const folderSchema = z.object({
  id: z.string().min(1),
  name: requiredString,
  isGeneral: z.boolean().optional(),
});

const checklistListItemSchema = z.object({
  instruction: z.string().optional(),
  status: z.boolean(),
});

export const checklistSchema = z
  .object({
    id: z.string().min(1),
    title: z.string({ error: 'שדה חובה!' }).min(3, 'שדה חובה!'),
    description: z.string().optional(),
    status: z.boolean(),
    departments: z
      .array(z.string().min(1, 'מזהה מחלקה לא תקין'))
      .min(1, 'יש לבחור לפחות מחלקה אחת'),
    shiftId: z.string().optional(),
    folderId: z.string().optional(),
    showInBrief: z.boolean(),
    approvalRequired: z.boolean(),
    enableComments: z.boolean(),
    resetDaily: z.boolean(),
    list: z
      .array(checklistListItemSchema)
      .min(1, { message: 'שדה חובה!' })
      .refine(
        (items) => items.some((it) => (it.instruction ?? '').trim().length > 0),
        { message: 'לפחות פריט אחד חייב לכלול הוראה לא ריקה' },
      ),
  })
  .superRefine((checklist, ctx) => {
    if (!checklist.shiftId && !checklist.folderId) {
      ctx.addIssue({
        code: 'custom',
        message: 'יש לשייך צ’קליסט למשמרת או לתיקיה',
        path: ['shiftId'],
      });
    }
  });

const onboardingUserSchema = z.object({
  id: z.string().min(1),
  firstName: requiredString,
  lastName: requiredString,
  clientIds: z.array(z.string()),
  branchIds: z.array(z.string()),
  subBranchIds: z.array(z.string()).optional(),
  departmentId: z.union([z.literal('all'), z.array(z.string())]),
  roleId: z.string(),
  countryCode: requiredString,
  phone: requiredString,
  email: z.email('כתובת מייל לא תקינה').min(1, 'שדה חובה!'),
  sendPassword: z.boolean(),
  sendPasswordVia: z.string().optional(),
  birthday: z.string().optional(),
  status: z.enum(['active', 'inactive']),
  userPermissions: z.array(z.string()),
  notifyFromDepts: z.array(
    z.object({
      clientId: z.string(),
      branchId: z.string().optional(),
      subBranchId: z.string().optional(),
      departmentIds: z.union([z.literal('all'), z.array(z.string())]),
    }),
  ),
  notifyQuickDepts: z.array(
    z.object({
      clientId: z.string(),
      branchId: z.string().optional(),
      subBranchId: z.string().optional(),
      departmentIds: z.union([z.literal('all'), z.array(z.string())]),
    }),
  ),
  notifyTypes: z.array(z.string()),
  enablePersonalNotifications: z.boolean(),
  associatedGroups: z.array(z.string()).optional(),
});

const operatingHoursSchema = z.object({
  startTime: requiredString,
  endTime: requiredString,
});

const checklistNotesResetSchema = z.object({
  type: z.enum([
    CHECKLIST_NOTES_RESET_TYPES.START_OF_DAY,
    CHECKLIST_NOTES_RESET_TYPES.END_OF_DAY,
    CHECKLIST_NOTES_RESET_TYPES.SPECIFIC_TIME,
  ]),
  time: z.string(),
});

const regulationCustomTimingSchema = z
  .object({
    repeatEvery: z.number().min(1),
    repeatType: z.string().min(1),
    weeklyDays: z.array(z.number()),
    monthlyDates: z.array(z.number()),
    endCondition: z.string().min(1),
    endDate: z.string().optional(),
    occurrences: z.number().optional(),
  })
  .nullable()
  .optional();

const uploadedMediaSchema = z.object({
  url: z.string().min(1),
  type: z.enum(['image', 'video', 'audio', 'document']),
});

const mediaFieldSchema = z.union([
  z.instanceof(File),
  uploadedMediaSchema,
  z.null(),
]);

export const regulationSchema = z
  .object({
    id: z.string().min(1),
    name: z.string({ error: 'שדה חובה!' }).min(1, 'שדה חובה!'),
    description: z.string().optional(),
    department: z.string().optional(),
    assignee: z.string().optional(),
    type: z.string({ error: 'שדה חובה!' }).min(1, 'שדה חובה!'),
    changeTypeTime: z.string({ error: 'שדה חובה!' }).min(1, 'שדה חובה!'),
    sendNotificationBeforeDate: z.boolean(),
    notificationTiming: z.string().optional(),
    files: z.array(z.union([z.instanceof(File), uploadedMediaSchema])),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    schedulingType: z.string(),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    time: z.string().optional(),
    folderId: z.string().min(1, 'שדה חובה!'),
    customTiming: regulationCustomTimingSchema,
  })
  .superRefine((data, ctx) => {
    // -------------------------
    // ✅ New timing rules
    // -------------------------
    const requiresStartDate = [
      'default',
      'all_day',
      'by_day_and_time',
      'between_days_and_hours',
    ].includes(data.schedulingType);

    const requiresEndDate = ['default', 'between_days_and_hours'].includes(
      data.schedulingType,
    );

    const requiresStartEndTime = [
      'by_day_and_time',
      'between_days_and_hours',
      'daily_at_fixed_time',
      'custom',
    ].includes(data.schedulingType);

    if (requiresStartDate && (!data.startDate || !data.startDate.length)) {
      ctx.addIssue({
        code: 'custom',
        message:
          data.schedulingType === 'all_day'
            ? 'עבור תזמון לכל היום יש להגדיר תאריך'
            : 'יש להגדיר תאריך התחלה',
        path: ['startDate'],
      });
    }

    if (requiresEndDate && (!data.endDate || !data.endDate.length)) {
      ctx.addIssue({
        code: 'custom',
        message: 'יש להגדיר תאריך סיום',
        path: ['endDate'],
      });
    }

    if (requiresStartEndTime) {
      if (
        !data.startTime ||
        !data.startTime.length ||
        !moment(data.startTime, 'HH:mm', true).isValid()
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להגדיר שעת התחלה',
          path: ['startTime'],
        });
      }
      if (
        !data.endTime ||
        !data.endTime.length ||
        !moment(data.endTime, 'HH:mm', true).isValid()
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להגדיר שעת סיום',
          path: ['endTime'],
        });
      }
    }

    if (data?.startTime && data?.endTime && data?.startTime === data?.endTime) {
      ctx.addIssue({
        code: 'custom',
        path: ['endTime'],
        message: 'שעת הסיום חייבת להיות אחרי שעת ההתחלה',
      });
    }
  });

const refineBaseNodeFields = (
  data: {
    operatingHours: { startTime: string; endTime: string };
    checklistNotesReset: { type: string; time: string };
    shifts: { from: string; to: string }[];
    zones: { color: string }[];
  },
  ctx: z.RefinementCtx,
) => {
  refineHhMmRange(
    data.operatingHours.startTime,
    data.operatingHours.endTime,
    ctx,
    ['operatingHours'],
  );

  if (
    data.checklistNotesReset.type === CHECKLIST_NOTES_RESET_TYPES.SPECIFIC_TIME
  ) {
    if (!data.checklistNotesReset.time?.length) {
      ctx.addIssue({
        code: 'custom',
        message: HH_MM_REQUIRED_MSG,
        path: ['checklistNotesReset', 'time'],
      });
    } else if (!isValidHhMm(data.checklistNotesReset.time)) {
      ctx.addIssue({
        code: 'custom',
        message: HH_MM_INVALID_MSG,
        path: ['checklistNotesReset', 'time'],
      });
    }
  }

  data.shifts.forEach((shift, index) => {
    refineShiftHhMmRange(shift.from, shift.to, ctx, ['shifts', index]);
  });

  data.zones.forEach((zone, index) => {
    if (zone.color && !zoneColorValues.has(zone.color)) {
      ctx.addIssue({
        code: 'custom',
        message: 'יש לבחור צבע מרשימת הצבעים',
        path: ['zones', index, 'color'],
      });
    }
  });
};

const baseNodeFieldsObjectSchema = z
  .object({
    id: z.string().min(1),
    name: requiredString,
    type: requiredString,
    logo: mediaFieldSchema,
    mobileLogo: mediaFieldSchema,
    country: requiredString,
    state: requiredString,
    city: requiredString,
    address: requiredString,
    hpNumber: z.string(),
    website: optionalWebsiteSchema,
    phone: phoneSchema,
    email: z.email('כתובת מייל לא תקינה').min(1, 'שדה חובה!'),
    contacts: contactsSchema,
    operatingHours: operatingHoursSchema,
    checklistNotesReset: checklistNotesResetSchema,
    departments: z
      .array(departmentSchema)
      .min(1, 'יש למלא לפחות מחלקה אחת תקינה'),
    roles: z.array(roleSchema).min(1, 'יש להגדיר לפחות תפקיד אחד תקין'),
    shifts: z.array(shiftSchema).min(1, 'יש להגדיר לפחות משמרת אחת תקינה'),
    zones: z.array(zoneSchema).min(1, 'יש להגדיר לפחות אזור אחד תקין'),
    zoneDrawing: mediaFieldSchema,
    checklistFolders: z.array(folderSchema),
    checklists: z.array(checklistSchema),
    users: z.array(onboardingUserSchema),
    regulationFolders: z.array(folderSchema),
    regulations: z.array(regulationSchema),
  })
  .superRefine(refineBaseNodeFields);

export const nodeRequiredDetailsSchema = z
  .object({
    id: z.string().min(1),
    name: requiredString,
    type: requiredString,
    logo: mediaFieldSchema,
    mobileLogo: mediaFieldSchema,
    country: requiredString,
    state: requiredString,
    city: requiredString,
    address: requiredString,
    hpNumber: z.string(),
    website: optionalWebsiteSchema,
    phone: phoneSchema,
    email: z.email('כתובת מייל לא תקינה').min(1, 'שדה חובה!'),
    contacts: contactsSchema,
  });

const refineNodeRelations = (
  data: {
    departments: { id: string }[];
    roles: { departmentId: string }[];
    shifts: { id: string }[];
    checklistFolders: { id: string }[];
    checklists: {
      departments: string[];
      shiftId?: string;
      folderId?: string;
    }[];
  },
  ctx: z.RefinementCtx,
) => {
  const departmentIds = new Set(data.departments.map((d) => d.id));
  const shiftIds = new Set(data.shifts.map((s) => s.id));
  const folderIds = new Set(data.checklistFolders.map((f) => f.id));

  data.roles.forEach((role, index) => {
    if (!departmentIds.has(role.departmentId)) {
      ctx.addIssue({
        code: 'custom',
        message: 'מחלקה לא קיימת',
        path: ['roles', index, 'departmentId'],
      });
    }
  });

  data.checklists.forEach((checklist, index) => {
    if (checklist.shiftId && !shiftIds.has(checklist.shiftId)) {
      ctx.addIssue({
        code: 'custom',
        message: 'משמרת לא קיימת',
        path: ['checklists', index, 'shiftId'],
      });
    }

    if (checklist.folderId && !folderIds.has(checklist.folderId)) {
      ctx.addIssue({
        code: 'custom',
        message: 'תיקיה לא קיימת',
        path: ['checklists', index, 'folderId'],
      });
    }

    if (!checklist.departments.includes('all')) {
      checklist.departments.forEach((deptId, deptIndex) => {
        if (!departmentIds.has(deptId)) {
          ctx.addIssue({
            code: 'custom',
            message: 'מחלקה לא קיימת',
            path: ['checklists', index, 'departments', deptIndex],
          });
        }
      });
    }
  });
};

export const nodeSetupCompleteSchema =
  baseNodeFieldsObjectSchema.superRefine(refineNodeRelations);

const subBranchNodeSchema = nodeSetupCompleteSchema;

const branchNodeSchema = baseNodeFieldsObjectSchema
  .safeExtend({
    subBranches: z.array(subBranchNodeSchema),
  })
  .superRefine(refineNodeRelations);

const networkNodeSchema = baseNodeFieldsObjectSchema
  .safeExtend({
    branches: z.array(branchNodeSchema),
  })
  .superRefine(refineNodeRelations);

export const networkDetailsSchema = z.object({
  network: z.array(networkNodeSchema),
});

export type NetworkDetailsSchemaValues = z.infer<typeof networkDetailsSchema>;

export function isNodeSetupComplete(
  node: z.infer<typeof nodeSetupCompleteSchema>,
): boolean {
  return nodeSetupCompleteSchema.safeParse(node).success;
}

// Strips departments/roles/shifts/zones/checklists/regulations rows that
// don't pass their own item schema, keeping whatever is fully valid.
export function filterInvalidNodeArrays<T extends BaseNodeFields>(node: T): T {
  return {
    ...node,
    departments: (node.departments ?? []).filter(
      (d) => departmentSchema.safeParse(d).success,
    ),
    roles: (node.roles ?? []).filter((r) => roleSchema.safeParse(r).success),
    shifts: (node.shifts ?? []).filter((s) => shiftSchema.safeParse(s).success),
    zones: (node.zones ?? []).filter((z) => zoneSchema.safeParse(z).success),
    checklists: (node.checklists ?? []).filter(
      (c) => checklistSchema.safeParse(c).success,
    ),
    regulations: (node.regulations ?? []).filter(
      (r) => regulationSchema.safeParse(r).success,
    ),
  };
}

function filterInvalidBranchNode(
  node: BranchNodeFormValues,
): BranchNodeFormValues {
  return {
    ...filterInvalidNodeArrays(node),
    subBranches: (node.subBranches ?? []).map((subBranch) =>
      filterInvalidNodeArrays(subBranch),
    ),
  };
}

export function filterInvalidNetworkTree(
  network: NetworkNodeFormValues[],
): NetworkNodeFormValues[] {
  return (network ?? []).map((node) => ({
    ...filterInvalidNodeArrays(node),
    branches: (node.branches ?? []).map((branch) =>
      filterInvalidBranchNode(branch),
    ),
  }));
}
